import { ApplicationConfig, ErrorHandler, mergeApplicationConfig } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import 'zone.js';
import type { ErrorEvent } from '@sentry/angular';
import { ENVIRONMENT as environment } from '@gs/shared/template/environment';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';

// Preconnect before bootstrap to the CMS API (backend) and LCP image (frontend) origins so the
// connection is warm when the prefetched request fires. Deduped per origin.
const addPreconnect = (rawUrl: string | undefined): void => {
  if (!rawUrl) return;
  try {
    const origin = new URL(rawUrl).origin;
    if (document.head.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = origin;
    document.head.appendChild(preconnect);
  } catch {
    // Malformed/placeholder URL — preconnect is best-effort, ignore.
  }
};
addPreconnect(environment.BACKEND_URL);
addPreconnect(environment.FRONTEND_URL);

/**
 * Detects chunk/module load failures (e.g. after a deployment when cached HTML
 * references stale JS bundles) and triggers a hard reload — once per session
 * to avoid infinite reload loops.
 */
const CHUNK_RELOAD_KEY = 'chunk-reload';
let isPageUnloading = false;
/** In-memory fallback when sessionStorage is unavailable (e.g. in-app browsers, privacy modes throwing SecurityError). */
let chunkReloadFlag = false;

// `pagehide`/`pageshow` instead of `beforeunload` — a `beforeunload` listener disqualifies the
// page from the back/forward cache, whereas `pagehide` is bfcache-friendly and still fires on unload.
window.addEventListener('pagehide', () => {
  isPageUnloading = true;
});
// Reset the flag when the page is restored from bfcache so fetch-error suppression does not leak across restores.
window.addEventListener('pageshow', () => {
  isPageUnloading = false;
});

/** Safely read the chunk-reload guard — falls back to in-memory flag if sessionStorage throws. */
function hasChunkReloadFlag(): boolean {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) !== null;
  } catch {
    return chunkReloadFlag;
  }
}

/** Safely write the chunk-reload guard — falls back to in-memory flag if sessionStorage throws. */
function setChunkReloadFlag(): void {
  chunkReloadFlag = true;
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  } catch {
    // ignore — in-memory flag already set
  }
}

/** Safely clear the chunk-reload guard — never throws. */
function clearChunkReloadFlag(): void {
  chunkReloadFlag = false;
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // ignore
  }
}

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg = error instanceof Error ? error.message : String(error);
  return (
    msg.includes('Importing a module script failed') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError') ||
    // Angular NG0750 — @defer block dependency loading failed (underlying cause is a chunk fetch failure)
    msg === 'NG0750' ||
    msg.includes('NG0750')
  );
}

/** Fetch aborts during page unload produce harmless TypeErrors that should not be reported. */
function isAbortedFetchError(error: unknown): boolean {
  if (!error) return false;
  const msg = error instanceof Error ? error.message : String(error);
  return msg === 'Load failed' || msg === 'Failed to fetch' || msg === 'NetworkError when attempting to fetch resource.';
}

/**
 * Whether the error is the `AbortError` of a request that was cancelled on purpose. Angular's fetch backend
 * aborts the request when its subscription ends early (navigation, `switchMap`, component destroy), and the
 * rejection can surface as unhandled — the abort itself is intended and nothing in the frontend can fix it.
 * @param error The captured error
 * @returns `true` for an `AbortError` DOMException
 */
function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Whether the error is an HTTP request that never got a response (status 0: offline, aborted, app sent to
 * background) — as `HttpErrorResponse` or as the `IError` the providers convert it into. The auth interceptor
 * already logs and toasts it, and nothing in the frontend can fix it.
 * @param error The captured error
 * @returns `true` for a status-0 HTTP failure
 */
function isNetworkFailure(error: unknown): boolean {
  return typeof error === 'object' && error !== null && !(error instanceof Error) && 'status' in error && Number(error.status) === 0;
}

/**
 * Error signatures that browsers produce with their own injected scripts:
 * - the stack overflow of the DOM walkers injected by in-app browsers (Google App, Instagram, TikTok …),
 *   whose frames carry the page URL instead of one of our bundles
 * - `Array.from(null)` from scripts injected by Firefox for iOS, whose stack holds only the native frame
 */
const INJECTED_SCRIPT_ERRORS = [/Maximum call stack size exceeded/i, /Array\.from requires an array-like object/];

/**
 * Whether the error matches one of the {@link INJECTED_SCRIPT_ERRORS} and has no frame in a `.js` file.
 * Both conditions are required: the same error from Cloudfront code keeps a frame of one of our bundles,
 * and our own inline scripts throw something else entirely.
 */
function isInjectedScriptError(event: ErrorEvent): boolean {
  const values = event.exception?.values ?? [];
  if (!values.some((value) => INJECTED_SCRIPT_ERRORS.some((pattern) => pattern.test(value.value ?? '')))) return false;
  const frames = values.flatMap((value) => value.stacktrace?.frames ?? []);
  // Without a single frame there is nothing to attribute the error to, so it stays reportable.
  return frames.length > 0 && !frames.some((frame) => /\.m?js($|[?#])/i.test(frame.filename ?? ''));
}

/**
 * Whether the page runs in an automated or crawler browser (Puppeteer/Playwright/Selenium, Googlebot,
 * Lighthouse …). Those often stub browser APIs and only produce noise in Sentry.
 * @returns `true` for automated browsers
 */
function isAutomatedBrowser(): boolean {
  // `bot/` instead of `bot` so device names like "CUBOT" are not matched.
  return navigator.webdriver || /HeadlessChrome|Lighthouse|bot\/|crawler|spider/i.test(navigator.userAgent);
}

function handleChunkLoadError(): void {
  if (hasChunkReloadFlag()) return;
  setChunkReloadFlag();
  window.location.reload();
}

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  if (isChunkLoadError(event.reason)) {
    event.preventDefault();
    handleChunkLoadError();
    return;
  }
  // Suppress fetch errors caused by page unload (e.g. reload from cookie consent)
  if ((isPageUnloading && isAbortedFetchError(event.reason)) || isAbortError(event.reason)) {
    event.preventDefault();
  }
});

/** Upper bound for waiting on the hydration before the Swiper runtime is registered anyway. */
const SWIPER_REGISTER_TIMEOUT_MS = 5000;

/** Lazily-loaded Sentry module; null until the dynamic import resolves. */
type SentryModule = typeof import('./sentry');
let sentry: SentryModule | null = null;

/**
 * Forwards Angular-zone errors to Sentry once it has loaded. Errors thrown before the dynamic import
 * resolves are logged but not sent (no buffering) — the accepted trade-off for keeping Sentry out of
 * the bootstrap critical path. Chunk-load failures still trigger the one-shot reload.
 */
class ForwardingErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (isChunkLoadError(error)) {
      handleChunkLoadError();
      return;
    }
    if ((isPageUnloading && isAbortedFetchError(error)) || isAbortError(error)) return;
    console.error(error);
    sentry?.captureException(error);
  }
}

/** Browser-only error handler — must not run during SSR. */
const sentryConfig: ApplicationConfig = {
  providers: [{ provide: ErrorHandler, useClass: ForwardingErrorHandler }]
};

// Lazy-load Sentry off the bootstrap critical path (keeps ~30-50 KB out of main.js). Errors thrown
// before this resolves are not captured; afterwards Sentry's global handlers and the
// ForwardingErrorHandler report errors as usual. Routing performance tracing (TraceService) is dropped.
// Automated browsers skip Sentry entirely.
if (!isAutomatedBrowser()) void import('./sentry').then((S) => {
  S.init({
    dsn: 'https://342bd63411ccf0bc13ff662c7f9dc0be@o4507532383748096.ingest.de.sentry.io/4511301610569808',
    sendDefaultPii: false,
    enableLogs: true,
    // Only report errors whose top frame is one of our bundles on our own origin (main/chunk/polyfills, scripts = consent.js;
    // hashed in production, served from `<base href="/">`). Drops errors from browser extensions, in-app browser
    // injections and third-party tags — anchored, because extensions often ship their own `main.js`/`chunk-*.js`.
    allowUrls: [new RegExp(`^${location.origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/(main|chunk|polyfills|scripts)(-[A-Z0-9]+)?\\.js`)],
    ignoreErrors: [
      // Instagram / Facebook in-app browser injected scripts (IAB unified bridge, hxp-chat-suppression)
      /window\.webkit\.messageHandlers/,
      // Instagram Android IAB (navigation_performance_logger_android) firing after WebView teardown
      /Java object is gone/,
      // Google Search in-app browser injected overlay/visibility detection
      /findTopmostVisibleElement/,
      /isOpaqueElement/,
      // TikTok in-app browser injected scripts
      /Can't find variable: ttq/,
      // Google Ads/Search in-app browser viewability helper (same injected script as findTopmostVisibleElement)
      /getPercent is not defined/,
      // Headless crawlers stubbing IntersectionObserver without observe() — breaks Angular's @defer (on viewport)
      /observer\.observe is not a function/,
      // DuckDuckGo iOS injected scripts (privacy protection), rejected without stack trace — allowUrls cannot match it
      /invalid origin/,
      // Common in-app browser noise
      /window\.fbq is not a function/
    ],
    beforeSend(event, hint) {
      const error = hint?.originalException;
      if (isPageUnloading && isAbortedFetchError(error)) return null;
      if (isNetworkFailure(error) || isAbortError(error)) return null;
      if (isChunkLoadError(error)) {
        // Defer-block / chunk failures that reach Sentry's global handler (e.g. NG0750)
        // bypass the unhandledrejection listener — trigger the same one-shot reload here.
        handleChunkLoadError();
        return null;
      }
      // Checked last so the chunk-reload above always wins.
      if (isInjectedScriptError(event)) return null;
      return event;
    }
  });
  sentry = S;
});

bootstrapApplication(AppComponent, mergeApplicationConfig(appConfig, sentryConfig))
  .then((appRef) => {
    // Clear chunk-reload guard after successful bootstrap
    clearChunkReloadFlag();
    // Swiper's custom-element runtime comes from its own chunk, registered only once the hydration is done:
    // an upgraded `swiper-container` renders nothing until SwiperDirective initializes it (init="false"), so
    // upgrading the server-rendered sliders before their (lazily loaded) components hydrated collapsed them
    // to 0px and shifted the whole page. The directives wait on `whenDefined` and initialize in the same task.
    // ponytail: the timeout only guards against an app that never becomes stable.
    const hydrated = Promise.race([appRef.whenStable(), new Promise((resolve) => setTimeout(resolve, SWIPER_REGISTER_TIMEOUT_MS))]);
    void hydrated.then(() => import('swiper/element/bundle')).then(({ register }) => register());
  })
  .catch((err) => {
    console.error(err);
    sentry?.captureException(err);
  });
